import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LogOut, 
  Inbox, 
  Tag, 
  Image, 
  Settings, 
  ExternalLink,
  Users
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

export default function AdminNav() {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success("Вы вышли из системы управления сайтом");
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin", label: "Заявки с сайта", icon: Inbox },
    { to: "/admin/prices", label: "Прайс-лист", icon: Tag },
    { to: "/admin/portfolio", label: "Работы До / После", icon: Image },
    { to: "/admin/settings", label: "Контакты & График", icon: Settings },
    { to: "/admin/clients", label: "Программа лояльности", icon: Users },
  ];

  return (
    <header className="sticky top-0 z-40 bg-[#F5EFE6] border-b-2 border-[#4A1419] shadow-sm">
      <div className="mx-auto max-w-[1500px] px-4 md:px-8 py-3 flex flex-wrap items-center justify-between gap-4">
        {/* Logo & Badge */}
        <div className="flex items-center gap-3.5">
          <Link to="/admin" className="flex items-baseline gap-2">
            <span className="font-heading text-2xl md:text-3xl text-[#4A1419] font-bold tracking-tight">ЩЁТКА</span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6E5549]">Управление сайтом</span>
          </Link>
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-[#C5A059]/20 text-[#4A1419] border border-[#C5A059]">
            🌐 Витрина сайта
          </span>
        </div>

        {/* Navigation Tabs */}
        <nav className="flex items-center gap-1 md:gap-2 flex-wrap text-xs md:text-sm">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`inline-flex items-center gap-2 px-3.5 py-2 font-medium transition-all ${
                  isActive
                    ? "bg-[#4A1419] text-[#F5EFE6] border-2 border-[#4A1419] shadow-inner font-semibold"
                    : "text-[#1A0E07] hover:bg-[#4A1419]/10 border-2 border-transparent"
                }`}
              >
                <Icon size={16} strokeWidth={isActive ? 2.2 : 1.75} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Direct Link to CRM & User Action */}
        <div className="flex items-center gap-3">
          {/* Prominent link button to Workshop CRM */}
          <a
            href="/crm/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs md:text-sm font-bold bg-[#141210] text-[#D4AF37] border-2 border-[#D4AF37] hover:bg-[#201D1A] transition-all shadow-md active:scale-95"
            title="Открыть производственную CRM мастерской (цех, заказы, мастера, финансы)"
          >
            <span>Перейти в CRM цеха</span>
            <ExternalLink size={14} strokeWidth={2.2} />
          </a>

          <div className="hidden lg:flex flex-col text-right pl-2 border-l border-[#4A1419]/20">
            <span className="text-xs font-semibold text-[#1A0E07] leading-tight">{admin?.name || "Администратор"}</span>
            <span className="text-[10px] text-[#6E5549] leading-tight">{admin?.email}</span>
          </div>

          <button
            onClick={handleLogout}
            className="p-2 text-[#4A1419] hover:bg-[#4A1419] hover:text-[#F5EFE6] border-2 border-[#4A1419] transition-colors"
            title="Выйти из админки сайта"
          >
            <LogOut size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </header>
  );
}
